/**
 * CommandPalette
 *
 * Main command palette dialog. Wraps shadcn Command in a dialog,
 * groups commands by category, and renders setup dialogs triggered by commands.
 * Supports drill-down pages for database object actions.
 *
 * Shift+Enter on a command that needs context forces the selector dialogs
 * instead of using the explorer's current selection.
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup } from '@/components/ui/command';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { getVisibleCommands } from '../registry/commandRegistry';
import { CommandPaletteItem } from './CommandPaletteItem';
import { ConnectionSearch } from './ConnectionSearch';
import { DatabaseObjectSearch, type SelectedObject } from './DatabaseObjectSearch';
import { ObjectActionsPage } from './ObjectActionsPage';
import { SchemaCompareSetupDialog } from '../../workspace/schema-compare';
import { DataCompareSetupDialog } from '../../workspace/data-compare';
import { TableDesignerDialog } from '../../workspace/table-designer';
import { QueryHistorySearchPage } from '../../query-history/components/QueryHistorySearchPage';
import { useExplorerContextHint } from '../hooks/useExplorerContextHint';
import {
  readClipboardConnectionPreview,
  setCachedClipboardPreview,
} from '../../connections/services/connection-string/clipboardPreview';
import { forceNextContextRequest } from '../../toolbar-context';
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import type { CommandCategory } from '../types/commandTypes';

const categoryLabels: Record<CommandCategory, string> = {
  workspace: 'Workspace',
  script: 'DDL Templates',
  navigation: 'Navigation',
  settings: 'Settings',
  connection: 'Connections',
  'database-object': 'Database Objects',
};

const isMac = navigator.platform.toUpperCase().includes('MAC');
const shiftEnterLabel = isMac ? '⇧↵' : 'Shift+Enter';

export function CommandPalette() {
  const isOpen = useCommandPaletteStore((s) => s.isOpen);
  const close = useCommandPaletteStore((s) => s.close);
  const initialPage = useCommandPaletteStore((s) => s.initialPage);
  const pendingDialog = useCommandPaletteStore((s) => s.pendingDialog);
  const tableDesignerContext = useCommandPaletteStore((s) => s.tableDesignerContext);
  const clearPendingDialog = useCommandPaletteStore((s) => s.clearPendingDialog);

  // Page state for drill-down
  const [selectedObject, setSelectedObject] = useState<SelectedObject | null>(null);
  const isHistoryPage = initialPage === 'query-history' && !selectedObject;
  const [search, setSearch] = useState('');
  // Track which command is highlighted via cmdk value
  const [highlightedValue, setHighlightedValue] = useState('');
  // Bumped when the clipboard preview resolves so the clipboard command row
  // re-renders and re-reads its (async-populated) preview cache.
  const [, setClipboardPreviewTick] = useState(0);

  // Ref for scrolling list to top on search change
  const listRef = useRef<HTMLDivElement>(null);

  // Explorer context hint
  const { hint: contextHint } = useExplorerContextHint();

  // Track open event for breadcrumbs and analytics
  useEffect(() => {
    if (isOpen) {
      addBreadcrumb('click', 'command-palette:open');
      window.electronAPI?.analytics?.track('command-palette:open', {}).catch(() => {});
    }
  }, [isOpen]);

  // Read the clipboard ONCE per palette open (never a background listener) so
  // the "New Connection from Clipboard" row can show a masked, truncated
  // preview. Cleared on close so nothing lingers.
  useEffect(() => {
    if (!isOpen) {
      setCachedClipboardPreview(null);
      return;
    }
    let cancelled = false;
    readClipboardConnectionPreview()
      .then((preview) => {
        if (cancelled) return;
        setCachedClipboardPreview(preview);
        setClipboardPreviewTick((t) => t + 1);
      })
      .catch(() => {
        /* clipboard unavailable — no preview */
      });
    return () => {
      cancelled = true;
      setCachedClipboardPreview(null);
    };
  }, [isOpen]);

  // Reset transient palette state whenever it closes — including programmatic
  // closes via store.close() (executing a command, navigation away, etc.).
  // Radix Dialog's onOpenChange only fires for user-initiated dismissals
  // (Escape / click outside), so without this effect the search input retains
  // its previous filter on the next open, which would silently hide commands.
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedObject(null);
      setHighlightedValue('');
    }
  }, [isOpen]);

  // Scroll list to top when search changes (cmdk auto-scrolls to highlighted item)
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      listRef.current?.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [search]);

  const commands = useMemo(() => getVisibleCommands(), [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group commands by category
  const grouped = useMemo(() => {
    const groups = new Map<CommandCategory, typeof commands>();
    for (const cmd of commands) {
      const list = groups.get(cmd.category) ?? [];
      list.push(cmd);
      groups.set(cmd.category, list);
    }
    return groups;
  }, [commands]);

  // Check if any visible command needs context (to show footer hint)
  const anyNeedsContext = useMemo(
    () => commands.some((cmd) => cmd.needsContext),
    [commands]
  );

  // Find command by highlighted value
  const findCommand = useCallback(
    (value: string) => commands.find((cmd) => cmd.id === value),
    [commands]
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      close();
      setSelectedObject(null);
      setSearch('');
    }
  }, [close]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Shift+Enter: execute highlighted command with forced context selectors
    if (e.key === 'Enter' && e.shiftKey && !selectedObject) {
      const cmd = findCommand(highlightedValue);
      if (cmd?.needsContext) {
        e.preventDefault();
        e.stopPropagation();
        window.electronAPI?.analytics?.track('command-palette:execute', { commandId: cmd.id, forceContext: true }).catch(() => {});
        forceNextContextRequest();
        cmd.execute();
        return;
      }
    }
    // Backspace on empty input returns to main search
    if (e.key === 'Backspace' && !search && selectedObject) {
      e.preventDefault();
      setSelectedObject(null);
    }
    // Backspace on empty input returns from history page to main
    if (e.key === 'Backspace' && !search && isHistoryPage) {
      e.preventDefault();
      useCommandPaletteStore.getState().openPage('main');
    }
  }, [search, selectedObject, highlightedValue, findCommand]);

  const handleSelectObject = useCallback((selected: SelectedObject) => {
    setSelectedObject(selected);
    setSearch('');
  }, []);

  const isOnActionsPage = selectedObject !== null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="overflow-hidden p-0 max-w-lg top-[30%]" data-testid="command-palette">
          <VisuallyHidden>
            <DialogTitle>Command Palette</DialogTitle>
          </VisuallyHidden>
          <Command
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
            onKeyDown={handleKeyDown}
            value={highlightedValue}
            onValueChange={setHighlightedValue}
            filter={(value, search, keywords) => {
              const haystack = [value, ...(keywords ?? [])].join(' ').toLowerCase();
              const words = search.toLowerCase().split(/\s+/).filter(Boolean);
              return words.every((w) => haystack.includes(w)) ? 1 : 0;
            }}
          >
            <CommandInput
              placeholder={
                isHistoryPage
                  ? 'Search query history...'
                  : isOnActionsPage
                    ? 'Select an action...'
                    : 'Type a command or search...'
              }
              value={search}
              onValueChange={setSearch}
              data-testid="command-palette-input"
            />
            <CommandList ref={listRef} className="max-h-[300px]">
              <CommandEmpty>No results found</CommandEmpty>
              {isHistoryPage ? (
                <QueryHistorySearchPage search={search} />
              ) : isOnActionsPage ? (
                <ObjectActionsPage selected={selectedObject} />
              ) : (
                <>
                  {/* Strong object matches (name starts with search) appear above commands */}
                  <DatabaseObjectSearch onSelectObject={handleSelectObject} search={search} mode="promoted" />
                  {Array.from(grouped.entries()).map(([category, cmds]) => (
                    <CommandGroup key={category} heading={categoryLabels[category]}>
                      {cmds.map((cmd) => (
                        <CommandPaletteItem
                          key={cmd.id}
                          command={cmd}
                          contextHint={contextHint}
                          onSelect={() => {
                            window.electronAPI?.analytics?.track('command-palette:execute', { commandId: cmd.id }).catch(() => {});
                            cmd.execute();
                          }}
                        />
                      ))}
                    </CommandGroup>
                  ))}
                  <ConnectionSearch />
                  {/* Remaining objects appear after commands */}
                  <DatabaseObjectSearch onSelectObject={handleSelectObject} search={search} mode="remaining" />
                </>
              )}
            </CommandList>
            {/* Footer hint for Shift+Enter */}
            {!isOnActionsPage && anyNeedsContext && contextHint && (
              <div className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground/60 flex items-center justify-end gap-1">
                <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1 text-[10px] font-medium">
                  {shiftEnterLabel}
                </kbd>
                <span>Choose connection</span>
              </div>
            )}
          </Command>
        </DialogContent>
      </Dialog>

      {/* Setup dialogs triggered by workspace commands */}
      <SchemaCompareSetupDialog
        open={pendingDialog === 'schema-compare'}
        onOpenChange={(open) => { if (!open) clearPendingDialog(); }}
      />
      <DataCompareSetupDialog
        open={pendingDialog === 'data-compare'}
        onOpenChange={(open) => { if (!open) clearPendingDialog(); }}
      />
      {pendingDialog === 'table-designer' && tableDesignerContext && (
        <TableDesignerDialog
          open
          onOpenChange={(open) => { if (!open) clearPendingDialog(); }}
          connectionId={tableDesignerContext.connectionId}
          database={tableDesignerContext.database}
        />
      )}
    </>
  );
}
